import { useQuery } from "@apollo/client";
import { KeyboardArrowRight } from "@mui/icons-material";
import { upperFirst } from "lodash";
import { DateTime } from "luxon";
import { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useQueryParam,
  withDefault,
  NumberParam,
  ArrayParam,
  StringParam,
} from "use-query-params";

import {
  ListAllTenDlcCampaignRegistrationsQuery,
  TenDlcCampaignRegistrationExpediteStatus,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { PageHeader } from "~frontend/components/PageHeader";
import Table, { TableControls, TableBox } from "~frontend/components/Table";
import { LIST_ALL_TEN_DLC_CAMPAIGN_REGISTRATIONS } from "~frontend/graphql/tenDlcRegistrations";
import { useStaffEmails } from "~frontend/hooks/useStaffEmails";
import { TenDLCRegistrationRow } from "~frontend/routes/staff/compliance-registrations/ten-dlc/types";

import { COMPLIANCE_STAFF_EMAILS, SORTS } from "../constants";

export const TenDlcRegistrationTable = (): ReactNode => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: staffEmailData, prioritizeEmails } = useStaffEmails();

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );

  const [expediteStatusFilter, setExpediteStatusFilter] = useQueryParam(
    "expeditestatus",
    withDefault(StringParam, "ANY")
  );

  const [assignmentFilter, setAssignmentFilter] = useQueryParam(
    "staff",
    withDefault(StringParam, "")
  );

  const [statusFilter, setStatusFilter] = useQueryParam(
    "status",
    withDefault(ArrayParam, [] as string[])
  );

  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, SORTS[0].value)
  );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const { loading, error, data } =
    useQuery<ListAllTenDlcCampaignRegistrationsQuery>(
      LIST_ALL_TEN_DLC_CAMPAIGN_REGISTRATIONS,
      {
        variables: {
          input: {
            sort,
            filter: {
              stringContains: search || undefined,
              expediteStatus:
                expediteStatusFilter === "ANY"
                  ? undefined
                  : expediteStatusFilter,
              assignedToStaffEmail:
                assignmentFilter === "ANY" ? undefined : assignmentFilter,
            },
            limit: pageSize,
            offset: page * pageSize,
          },
        },
      }
    );

  return (
    <>
      <PageHeader>10DLC Registrations</PageHeader>
      <AsyncWrapper data={data} loading={[loading]} errors={[error]}>
        {({ listAllTenDlcCampaignRegistrations }) => {
          // Apply status filter if needed - we can't filter by status in the backend
          const filteredRegistrations: TenDLCRegistrationRow[] =
            listAllTenDlcCampaignRegistrations.registrations
              .filter(reg => {
                // Apply status filter if any are selected
                if (statusFilter.length === 0) {
                  return true;
                }
                if (!reg.status) {
                  return false;
                }
                return statusFilter.includes(reg.status);
              })
              .map(reg => {
                // Convert each registration to TenDLCRegistrationRow format
                return {
                  ...reg,
                  organizationName: reg.brandRegistration.organizationName,
                  tcrBrandId: reg.brandRegistration.tcrBrandId,
                  tenantSlug: reg.tenant.slug,
                  subaccountSlug: reg.subaccount.slug,
                  brandRegistrationId: reg.brandRegistration.id,
                  campaignRegistrationId: reg.id,
                  campaignSubmitted: !!reg.submittedAt,
                  submittedByFullName: reg.submittedByUser?.fullName,
                };
              });

          return (
            <>
              <TableControls
                filters={[
                  {
                    id: "status",
                    label: "Status",
                    value: statusFilter as string[],
                    onChange: setStatusFilter,
                    options: [
                      { value: "ANY", label: "Any" },
                      { value: "NEW", label: "New" },
                      { value: "NEEDS_REVIEW", label: "Needs staff review" },
                      { value: "PENDING", label: "Pending vendor" },
                      {
                        value: "PROVISIONING",
                        label: "Number(s) provisioning",
                      },
                      { value: "VENDOR_ERROR", label: "Vendor error" },
                      { value: "NUMBER_ERROR", label: "Provisioning error" },
                      { value: "COMPLETE", label: "Complete" },
                      { value: "UNKNOWN", label: "Unknown" },
                    ],
                    multi: true,
                  },
                  {
                    id: "expediteStatus",
                    label: "Expedite Status",
                    value: expediteStatusFilter,
                    onChange: setExpediteStatusFilter,
                    options: [
                      { value: "ANY", label: "Any" },
                      {
                        value: TenDlcCampaignRegistrationExpediteStatus.None,
                        label: "None",
                      },
                      {
                        value:
                          TenDlcCampaignRegistrationExpediteStatus.Requested,
                        label: "Requested",
                      },
                      {
                        value: TenDlcCampaignRegistrationExpediteStatus.Paid,
                        label: "Paid",
                      },
                    ],
                  },
                  {
                    id: "assignedTo",
                    label: "Assigned To",
                    value: assignmentFilter,
                    onChange: setAssignmentFilter,
                    options: [
                      { value: "ANY", label: "Any" },
                      ...prioritizeEmails(
                        staffEmailData,
                        COMPLIANCE_STAFF_EMAILS
                      ).map(staff => ({
                        value: staff.email,
                        label: staff.fullName,
                      })),
                    ],
                  },
                ]}
                search={{
                  value: search,
                  onChange: setSearch,
                  placeholder: "Search for a brand or campaign...",
                }}
                sort={{
                  value: sort,
                  onChange: setSort,
                  options: SORTS.map(sort => ({
                    value: sort.value,
                    label: sort.label,
                  })),
                }}
              />
              <TableBox>
                <Table<TenDLCRegistrationRow>
                  columns={[
                    {
                      key: "owner",
                      title: "Tenant/Subaccount",
                      renderValue: reg => {
                        return `${reg.tenantSlug}: ${reg.subaccountSlug}`;
                      },
                    },
                    {
                      key: "organizationName",
                      title: "Org Name",
                      renderValue: reg => reg.organizationName,
                      align: "left",
                    },
                    {
                      key: "tcrBrandId",
                      title: "TCR Brand ID",
                      renderValue: reg => (
                        <span data-testid="reg-10dlcId-cell">
                          {reg.tcrBrandId ?? "--"}
                        </span>
                      ),
                      align: "left",
                    },
                    {
                      key: "tcrCampaignId",
                      title: "TCR Campaign ID",
                      renderValue: campaign => campaign.tcrCampaignId || "--",
                    },
                    {
                      key: "status",
                      title: "Status",
                      renderValue: campaign => {
                        if (campaign.status) {
                          return upperFirst(campaign.status.toLowerCase());
                        }
                        return "--";
                      },
                    },
                    {
                      key: "tcrStatus",
                      title: "TCR Status",
                      renderValue: campaign => campaign.tcrStatus || "--",
                    },
                    {
                      key: "useCase",
                      title: "Use Case",
                      renderValue: campaign =>
                        upperFirst(campaign.useCase?.toLowerCase() || ""),
                    },
                    {
                      key: "expediteStatus",
                      title: "Expedite?",
                      renderValue: campaign => {
                        switch (campaign.expediteStatus) {
                          case TenDlcCampaignRegistrationExpediteStatus.None:
                            return "None";
                          case TenDlcCampaignRegistrationExpediteStatus.Requested:
                            return "Requested";
                          case TenDlcCampaignRegistrationExpediteStatus.Paid:
                            return "Paid";
                        }
                      },
                    },
                    {
                      key: "submittedAt",
                      title: "Submitted to STWT",
                      renderValue: campaign =>
                        campaign.campaignSubmitted ? (
                          <span data-testid="campaign-submittedAt-cell">
                            {campaign.submittedAt
                              ? DateTime.fromMillis(
                                  campaign.submittedAt
                                ).toLocaleString()
                              : "--"}
                            {campaign.submittedByFullName &&
                              ` by ${campaign.submittedByFullName}`}
                          </span>
                        ) : (
                          "--"
                        ),
                    },
                  ]}
                  rows={filteredRegistrations}
                  rowActions={row => [
                    {
                      id: row.campaignSubmitted
                        ? "edit-campaign-registration"
                        : "edit-registration",
                      type: "button",
                      icon: <KeyboardArrowRight />,
                      tooltipLabel: "View/Edit/Copy Campaign",
                      onClick: () => {
                        const newPath = [
                          location.pathname,
                          "ten-dlc",
                          row.brandRegistrationId,
                        ];
                        if (row.campaignSubmitted) {
                          newPath.push(row.campaignRegistrationId);
                        }
                        // pathName/ten-dlc/brandRegistrationId/campaignRegistrationId
                        navigate(
                          newPath.join("/").replaceAll("//", "/")
                        )?.catch(console.error);
                      },
                    },
                  ]}
                  emptyState="No registrations found"
                  // FIXME: This won't work as expected if there is a status filter set as we're filtering that above, not on the BE.
                  totalCount={listAllTenDlcCampaignRegistrations.totalCount}
                  rowsPerPage={pageSize}
                  onChangeRowsPerPage={newPageSize => setPageSize(newPageSize)}
                  page={page}
                  onChangePage={(page: number) => setPage(page)}
                />
              </TableBox>
            </>
          );
        }}
      </AsyncWrapper>
    </>
  );
};
